import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import InputField from '../components/ui/InputField';
import TextAreaField from '../components/ui/TextAreaField';
import Button from '../components/ui/Button';

const EgitimReportForm = ({ onDataChange }) => {
  const { t } = useTranslation();
  const [centerInfo, setCenterInfo] = useState({
    centerName: '',
    studentName: '',
    studentId: '',
    reportPeriod: new Date().toISOString().slice(0, 7)
  });

  const [fees, setFees] = useState([
    { courseName: '', duration: '', fee: 0, paid: 0, remaining: 0 }
  ]);

  const [notes, setNotes] = useState('');

  useEffect(() => {
    const totalFee = fees.reduce((sum, item) => sum + item.fee, 0);
    const totalPaid = fees.reduce((sum, item) => sum + item.paid, 0);
    const totalRemaining = totalFee - totalPaid;

    onDataChange({
      ...centerInfo,
      fees,
      notes,
      totalFee,
      totalPaid,
      totalRemaining,
      date: new Date().toLocaleDateString()
    });
  }, [centerInfo, fees, notes, onDataChange]);

  const handleFeeChange = (index, field, value) => {
    const newFees = [...fees];
    newFees[index][field] = value;
    if (field === 'fee' || field === 'paid') {
      newFees[index].remaining = newFees[index].fee - newFees[index].paid;
    }
    setFees(newFees);
  };

  const totalFee = fees.reduce((sum, item) => sum + item.fee, 0);
  const totalPaid = fees.reduce((sum, item) => sum + item.paid, 0);
  const totalRemaining = totalFee - totalPaid;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 lg:p-8 border border-gray-200 dark:border-gray-700 shadow-sm space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <InputField label="Education Center Name" name="centerName" value={centerInfo.centerName} onChange={(e) => setCenterInfo({ ...centerInfo, centerName: e.target.value })} placeholder="Center name" required />
        <InputField label="Student Name" name="studentName" value={centerInfo.studentName} onChange={(e) => setCenterInfo({ ...centerInfo, studentName: e.target.value })} placeholder="Student name" />
        <InputField label="Student ID" name="studentId" value={centerInfo.studentId} onChange={(e) => setCenterInfo({ ...centerInfo, studentId: e.target.value })} placeholder="Student ID" />
        <InputField label="Report Period" name="reportPeriod" type="month" value={centerInfo.reportPeriod} onChange={(e) => setCenterInfo({ ...centerInfo, reportPeriod: e.target.value })} />
      </div>

      <div>
        <h3 className="text-xl font-bold text-[#111418] dark:text-white mb-4">Course Fees</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="p-3 text-sm font-medium text-gray-600 dark:text-gray-400">Course Name</th>
                <th className="p-3 text-sm font-medium text-gray-600 dark:text-gray-400">Duration</th>
                <th className="p-3 text-sm font-medium text-gray-600 dark:text-gray-400">Fee</th>
                <th className="p-3 text-sm font-medium text-gray-600 dark:text-gray-400">Paid</th>
                <th className="p-3 text-sm font-medium text-gray-600 dark:text-gray-400 text-right">Remaining</th>
                <th className="p-3 w-12"></th>
              </tr>
            </thead>
            <tbody>
              {fees.map((fee, index) => (
                <tr key={index} className="border-b border-gray-200 dark:border-gray-700">
                  <td className="p-2"><input className="form-input w-full bg-transparent border-0 focus:ring-0 p-2" type="text" value={fee.courseName} onChange={(e) => handleFeeChange(index, 'courseName', e.target.value)} placeholder="e.g., English Course" /></td>
                  <td className="p-2"><input className="form-input w-full bg-transparent border-0 focus:ring-0 p-2" type="text" value={fee.duration} onChange={(e) => handleFeeChange(index, 'duration', e.target.value)} placeholder="e.g., 3 months" /></td>
                  <td className="p-2"><input className="form-input w-full bg-transparent border-0 focus:ring-0 p-2 text-right" type="number" min="0" step="0.01" value={fee.fee} onChange={(e) => handleFeeChange(index, 'fee', parseFloat(e.target.value) || 0)} /></td>
                  <td className="p-2"><input className="form-input w-full bg-transparent border-0 focus:ring-0 p-2 text-right" type="number" min="0" step="0.01" value={fee.paid} onChange={(e) => handleFeeChange(index, 'paid', parseFloat(e.target.value) || 0)} /></td>
                  <td className="p-2 text-right font-medium text-[#111418] dark:text-gray-100">${fee.remaining.toFixed(2)}</td>
                  <td className="p-2 text-center"><button onClick={() => setFees(fees.filter((_, i) => i !== index))} className="text-gray-400 hover:text-red-500" disabled={fees.length === 1}><span className="material-symbols-outlined text-xl">delete</span></button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <Button onClick={() => setFees([...fees, { courseName: '', duration: '', fee: 0, paid: 0, remaining: 0 }])} variant="ghost" icon="add_circle" className="mt-4">Add Course</Button>
        </div>
      </div>

      <div className="flex justify-end">
        <div className="w-full max-w-sm space-y-3 bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
          <div className="flex justify-between items-center"><span className="text-gray-600 dark:text-gray-400">Total Fee</span><span className="font-medium text-[#111418] dark:text-gray-100">${totalFee.toFixed(2)}</span></div>
          <div className="flex justify-between items-center"><span className="text-gray-600 dark:text-gray-400">Total Paid</span><span className="font-medium text-green-600 dark:text-green-400">${totalPaid.toFixed(2)}</span></div>
          <div className="flex justify-between items-center pt-3 border-t border-gray-200 dark:border-gray-700"><span className="text-lg font-bold text-[#111418] dark:text-gray-100">Total Remaining</span><span className="text-lg font-bold text-primary">${totalRemaining.toFixed(2)}</span></div>
        </div>
      </div>

      <TextAreaField label={t('form.notes')} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Academic notes, payment schedule..." rows={3} />
    </div>
  );
};

export default EgitimReportForm;
